import React, { useRef, useState, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmtStat = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "time") { const m = Math.floor(v / 60); const r = (v % 60).toFixed(1); return m > 0 ? `${m}:${r < 10 ? "0" : ""}${r}` : `${r}s`; }
  if (fmt === "wave") return `Wave ${Math.round(v)}`;
  return String(Math.round(v));
};
const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

// Returns { value, label, sub } describing the brag headline for this viewer.
function computeBest(game, user, lastScore) {
  const sb = game.stat_board;
  if (sb && sb.my_value != null) {
    return { value: fmtStat(sb.my_value, sb.fmt), label: sb.value_col, sub: sb.my_rank ? `Rank #${sb.my_rank} · ${(user?.name) || "Me"}` : (user?.name || "Me") };
  }
  const mine = (game.leaderboard || []).find((s) => s.user_id === user?.id);
  if (mine) return { value: String(mine.score), label: "Score", sub: user?.name || "Me" };
  if (lastScore != null) return { value: String(lastScore), label: "Score", sub: user?.name || "Me" };
  // Fallback: brag about the current record so it's still shareable
  if (sb && (sb.entries || []).length) return { value: fmtStat(sb.entries[0].value, sb.fmt), label: `Top ${sb.value_col}`, sub: `Set by ${sb.entries[0].user_name}` };
  if ((game.leaderboard || []).length) return { value: String(game.leaderboard[0].score), label: "Top score", sub: `Set by ${game.leaderboard[0].user_name}` };
  return { value: "?", label: "No record yet", sub: "Be the first to set one!" };
}

export default function ShareScoreCard({ game, gameId, user, lastScore }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const shareUrl = `${window.location.origin}/app/arcade/${gameId}`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");
  const best = computeBest(game, user, lastScore);
  const hasMine = best.label !== "No record yet" && !best.sub?.startsWith("Set by") && !best.sub?.startsWith("Be the first");

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const W = 1200, H = 630;
    const bg = game.world?.bgColor || "#0b1026";
    const ac = game.world?.accent || "#7c5cff";
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, bg); grad.addColorStop(1, ac);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(0,0,0,0.38)"; ctx.fillRect(0, 0, W, H);
    // brand
    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.textBaseline = "top";
    ctx.font = "bold 34px Inter, system-ui, sans-serif";
    ctx.fillText("🎮 GAMEVIEW STUDIO", 60, 52);
    // cover emoji
    ctx.textBaseline = "middle"; ctx.font = "170px serif";
    ctx.fillText(game.cover_emoji || "🎮", 70, 340);
    // title
    ctx.fillStyle = "#fff"; ctx.textBaseline = "alphabetic";
    ctx.font = "bold 60px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(game.title, 24), 320, 230);
    // headline
    ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.font = "34px Inter, system-ui, sans-serif";
    ctx.fillText(hasMine ? "I scored" : "Current record", 320, 300);
    // big value
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 110px Inter, system-ui, sans-serif";
    ctx.fillText(`${best.value}`, 320, 410);
    ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "bold 40px Inter, system-ui, sans-serif";
    ctx.fillText(best.label, 320, 460);
    // player / sub
    ctx.fillStyle = "rgba(255,255,255,0.78)"; ctx.font = "32px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(best.sub, 40), 320, 510);
    // footer CTA
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "28px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Play free — ${truncate(shortUrl, 46)}`, 60, 580);
  }, [game, best, hasMine, shortUrl]);

  useEffect(() => { if (open) { const t = setTimeout(draw, 60); return () => clearTimeout(t); } }, [open, draw]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(game.title || "gameview").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-score.png`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Score card downloaded!");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — paste it anywhere!"); }
    catch { toast.error("Couldn't copy the link."); }
  };

  const nativeShare = async () => {
    const text = hasMine ? `I scored ${best.value} ${best.label} on "${game.title}"! Can you beat me?` : `Check out "${game.title}" on GameView Studio!`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "gameview-score.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: game.title, text, url: shareUrl });
      } else {
        await navigator.share({ title: game.title, text, url: shareUrl });
      }
    } catch { /* user cancelled or unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="gp-share-score-btn">
          <Share2 className="w-4 h-4" /> Brag
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="gp-share-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[hsl(var(--primary))]" /> Share your score</DialogTitle></DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="gp-share-canvas" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-download"><Download className="w-4 h-4" /> Download image</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-copy-link"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="gp-share-native"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
